export type Maybe<T> = T | null;
export type InputMaybe<T> = T | null | undefined;

export type Refetch = () => unknown;

type DateLike = string;

export const EngagementIntent = {
  ActivelySearching: 'ACTIVELY_SEARCHING',
  ConsideringIfGood: 'CONSIDERING_IF_GOOD',
  ExploringOptions: 'EXPLORING_OPTIONS',
  ImmediateSearch: 'IMMEDIATE_SEARCH',
  StatusQuoPreferred: 'STATUS_QUO_PREFERRED',
} as const;
export type EngagementIntent = (typeof EngagementIntent)[keyof typeof EngagementIntent];

export const HolidayWorkEnum = {
  NotPossible: 'NOT_POSSIBLE',
  Possible: 'POSSIBLE',
} as const;
export type HolidayWorkEnum = (typeof HolidayWorkEnum)[keyof typeof HolidayWorkEnum];

export const ImportWarningSeverity = {
  Error: 'ERROR',
  Info: 'INFO',
  Warning: 'WARNING',
} as const;
export type ImportWarningSeverity = (typeof ImportWarningSeverity)[keyof typeof ImportWarningSeverity];

export const ImportWarningSource = {
  Extraction: 'EXTRACTION',
  Llm: 'LLM',
  Mapping: 'MAPPING',
  System: 'SYSTEM',
  Validation: 'VALIDATION',
} as const;
export type ImportWarningSource = (typeof ImportWarningSource)[keyof typeof ImportWarningSource];

export const MessageTypeEnum = {
  Message: 'MESSAGE',
  SystemAgreement: 'SYSTEM_AGREEMENT',
  SystemScout: 'SYSTEM_SCOUT',
  SystemText: 'SYSTEM_TEXT',
} as const;
export type MessageTypeEnum = (typeof MessageTypeEnum)[keyof typeof MessageTypeEnum];

export const PreferenceRating = {
  High: 'HIGH',
  Low: 'LOW',
  Medium: 'MEDIUM',
  VeryHigh: 'VERY_HIGH',
  VeryLow: 'VERY_LOW',
} as const;
export type PreferenceRating = (typeof PreferenceRating)[keyof typeof PreferenceRating];

export const ProjectStatusEnum = {
  Close: 'CLOSE',
  Draft: 'DRAFT',
  Open: 'OPEN',
} as const;
export type ProjectStatusEnum = (typeof ProjectStatusEnum)[keyof typeof ProjectStatusEnum];

export const ProjectTaxTypeEnum = {
  TaxExcluded: 'TAX_EXCLUDED',
  TaxIncluded: 'TAX_INCLUDED',
} as const;
export type ProjectTaxTypeEnum = (typeof ProjectTaxTypeEnum)[keyof typeof ProjectTaxTypeEnum];

export const RemoteWorkEnum = {
  NotPossible: 'NOT_POSSIBLE',
  Possible: 'POSSIBLE',
} as const;
export type RemoteWorkEnum = (typeof RemoteWorkEnum)[keyof typeof RemoteWorkEnum];

export const ScoutSortEnum = {
  CreatedAtDesc: 'CREATED_AT_DESC',
  LastAccessDesc: 'LAST_ACCESS_DESC',
  LatestMessageDesc: 'LATEST_MESSAGE_DESC',
  ReadAtDesc: 'READ_AT_DESC',
} as const;
export type ScoutSortEnum = (typeof ScoutSortEnum)[keyof typeof ScoutSortEnum];

export const SearchType = {
  And: 'AND',
  Or: 'OR',
} as const;
export type SearchType = (typeof SearchType)[keyof typeof SearchType];

export const TalentSort = {
  PriceHourlyAsc: 'PRICE_HOURLY_ASC',
  PriceHourlyDesc: 'PRICE_HOURLY_DESC',
  UpdatedAtDesc: 'UPDATED_AT_DESC',
} as const;
export type TalentSort = (typeof TalentSort)[keyof typeof TalentSort];

export const TemplateSortEnum = {
  CreatedAtAsc: 'CREATED_AT_ASC',
  CreatedAtDesc: 'CREATED_AT_DESC',
  TitleAsc: 'TITLE_ASC',
  TitleDesc: 'TITLE_DESC',
  UpdatedAtAsc: 'UPDATED_AT_ASC',
  UpdatedAtDesc: 'UPDATED_AT_DESC',
} as const;
export type TemplateSortEnum = (typeof TemplateSortEnum)[keyof typeof TemplateSortEnum];

export type UserError = {
  message: string;
};

export type PageInfo = {
  offset: number;
  total: number;
  totalPage?: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

export type MasterData = {
  id: string;
  name: string;
};

export type MasterEngineerSkillCategory = MasterData;

export type MasterEngineerSkill = MasterData & {
  category: MasterEngineerSkillCategory;
};

export type Organization = {
  id: string;
  createdAt: DateLike;
  updatedAt: DateLike;
  name: string;
  email: string;
  logo?: string | null;
  postalCode?: string | null;
  address?: string | null;
  numberOfEmployees?: number | null;
  averageAge?: number | null;
  capital?: number | null;
  ceoName?: string | null;
  employeeCount?: number | null;
  revenue?: number | null;
  establishmentYear?: number | null;
  establishmentMonth?: number | null;
  businessDetails?: string | null;
  whatWeDo?: string | null;
  missionVision?: string | null;
  serviceProduct?: string | null;
  ourTeam?: string | null;
  siteUrl?: string | null;
  github?: string | null;
  techblogUrl?: string | null;
};

export type OrganizationUser = {
  id: string;
  createdAt: DateLike;
  updatedAt: DateLike;
  name: string;
  email: string;
  emailVerified?: boolean;
  organizationId?: string;
  organization: Organization;
};

export type OrganizationUserConnection = {
  nodes: OrganizationUser[];
  pageInfo: PageInfo;
};

export type CompanyDetailsQuery = {
  me: OrganizationUser;
};

export type AccountSettingQuery = {
  me: Pick<OrganizationUser, 'id' | 'createdAt' | 'updatedAt' | 'name' | 'email'>;
};

export type TalentMastersQuery = {
  engineerPositions: MasterData[];
  engineerSkillLanguages: MasterEngineerSkill[];
  engineerSkills: MasterEngineerSkill[];
};

export type EngineerFilter = {
  ids?: InputMaybe<Array<InputMaybe<string>>>;
  searchType?: InputMaybe<SearchType>;
};

export type ProjectsFilter = {
  engineerPosition?: InputMaybe<EngineerFilter>;
  engineerSkill?: InputMaybe<EngineerFilter>;
  name?: InputMaybe<string>;
  status?: InputMaybe<ProjectStatusEnum>;
  targetAgeMax?: InputMaybe<number>;
  targetAgeMin?: InputMaybe<number>;
  text?: InputMaybe<string>;
  textMode?: InputMaybe<SearchType>;
};

export type CreateProjectInput = {
  aboutTeam?: InputMaybe<string>;
  canExtendDuration?: InputMaybe<boolean>;
  coreTimeEnd?: InputMaybe<string>;
  coreTimeStart?: InputMaybe<string>;
  desiredPersonnel?: InputMaybe<string>;
  details?: InputMaybe<string>;
  developmentSkills?: InputMaybe<string>;
  engineerPositionIds: string[];
  engineerSkillIds: string[];
  expectedDuration?: InputMaybe<number>;
  expectedRole?: InputMaybe<string>;
  hasFlexibleSchedule?: InputMaybe<boolean>;
  holidayWork?: InputMaybe<HolidayWorkEnum>;
  interviewCount?: InputMaybe<number>;
  jobOverview?: InputMaybe<string>;
  mainJobDescription?: InputMaybe<string>;
  name: string;
  officeLocation?: InputMaybe<string>;
  paymentDate?: InputMaybe<string>;
  priceHourlyMax?: InputMaybe<number>;
  priceHourlyMin?: InputMaybe<number>;
  priceMonthlyMax?: InputMaybe<number>;
  priceMonthlyMin?: InputMaybe<number>;
  priceSettlementHourFrom?: InputMaybe<number>;
  priceSettlementHourTo?: InputMaybe<number>;
  priceTaxType?: InputMaybe<ProjectTaxTypeEnum>;
  privateName?: InputMaybe<string>;
  publish?: boolean;
  remoteWork?: InputMaybe<RemoteWorkEnum>;
  requiredSkill?: InputMaybe<string>;
  startDate?: InputMaybe<string | Date>;
  targetAgeMax?: InputMaybe<number>;
  targetAgeMin?: InputMaybe<number>;
  teamComposition?: InputMaybe<string>;
  weeklyWorkingDayMax?: InputMaybe<number>;
  weeklyWorkingDayMin?: InputMaybe<number>;
  welcomeSkill?: InputMaybe<string>;
  workingHoursEnd?: InputMaybe<string>;
  workingHoursStart?: InputMaybe<string>;
};

export type UpdateProjectInput = CreateProjectInput & {
  id: string;
};

export type Project = {
  id: string;
  createdAt: DateLike;
  updatedAt: DateLike;
  organizationId: string;
  organization: Pick<Organization, 'id' | 'name' | 'logo'>;
  name: string;
  privateName?: string | null;
  status: ProjectStatusEnum;
  paymentDate?: string | null;
  jobOverview?: string | null;
  mainJobDescription?: string | null;
  details?: string | null;
  expectedRole?: string | null;
  requiredSkill?: string | null;
  welcomeSkill?: string | null;
  desiredPersonnel?: string | null;
  teamComposition?: string | null;
  aboutTeam?: string | null;
  developmentSkills?: string | null;
  officeLocation?: string | null;
  workingHoursStart?: string | null;
  workingHoursEnd?: string | null;
  hasFlexibleSchedule?: boolean | null;
  coreTimeStart?: string | null;
  coreTimeEnd?: string | null;
  remoteWork?: RemoteWorkEnum | null;
  holidayWork?: HolidayWorkEnum | null;
  targetAgeMax?: number | null;
  targetAgeMin?: number | null;
  startDate?: DateLike | null;
  expectedDuration?: number | null;
  canExtendDuration?: boolean | null;
  weeklyWorkingDayMin?: number | null;
  weeklyWorkingDayMax?: number | null;
  interviewCount?: number | null;
  priceTaxType?: ProjectTaxTypeEnum | null;
  priceHourlyMax?: number | null;
  priceHourlyMin?: number | null;
  priceMonthlyMax?: number | null;
  priceMonthlyMin?: number | null;
  priceSettlementHourFrom?: number | null;
  priceSettlementHourTo?: number | null;
  heroImageSrc?: string | null;
  projectMasterEngineerPositions: MasterData[];
  projectMasterEngineerSkills: MasterEngineerSkill[];
  scouts: Array<{ id: string }>;
};

export type ProjectConnection = {
  nodes: Project[];
  pageInfo: PageInfo;
};

export type ProjectListQuery = {
  projects: ProjectConnection;
};

export type ProjectDetailQuery = {
  project?: Project | null;
};

export type ProjectDraft = {
  __typename?: string;
  aboutTeam?: string | null;
  canExtendDuration?: boolean | null;
  coreTimeEnd?: string | null;
  coreTimeStart?: string | null;
  desiredPersonnel?: string | null;
  details?: string | null;
  developmentSkills?: string | null;
  engineerPositionIds?: string[] | null;
  engineerSkillIds?: string[] | null;
  expectedDuration?: number | null;
  expectedRole?: string | null;
  hasFlexibleSchedule?: boolean | null;
  holidayWork?: HolidayWorkEnum | null;
  interviewCount?: number | null;
  jobOverview?: string | null;
  mainJobDescription?: string | null;
  name?: string | null;
  officeLocation?: string | null;
  paymentDate?: string | null;
  priceHourlyMax?: number | null;
  priceHourlyMin?: number | null;
  priceMonthlyMax?: number | null;
  priceMonthlyMin?: number | null;
  priceSettlementHourFrom?: number | null;
  priceSettlementHourTo?: number | null;
  priceTaxType?: ProjectTaxTypeEnum | null;
  privateName?: string | null;
  remoteWork?: RemoteWorkEnum | null;
  requiredSkill?: string | null;
  startDate?: string | null;
  targetAgeMax?: number | null;
  targetAgeMin?: number | null;
  teamComposition?: string | null;
  weeklyWorkingDayMax?: number | null;
  weeklyWorkingDayMin?: number | null;
  welcomeSkill?: string | null;
  workingHoursEnd?: string | null;
  workingHoursStart?: string | null;
};

export type ProjectImportTelemetry = {
  __typename?: string;
  provider: string;
  model: string;
  promptTokens?: number | null;
  completionTokens?: number | null;
  totalTokens?: number | null;
  cost?: number | null;
  durationMs?: number | null;
  traceId?: string | null;
};

export type ImportWarning = {
  __typename?: string;
  code: string;
  message: string;
  fieldPath?: string | null;
  severity: ImportWarningSeverity;
  source: ImportWarningSource;
};

export type ImportProjectFromPdfPayload = {
  __typename?: string;
  clientMutationId?: string | null;
  draft: ProjectDraft;
  rawText: string;
  warnings: ImportWarning[];
  telemetry: ProjectImportTelemetry;
  userErrors: UserError[];
};

export type Preference = {
  id: string;
  category: string;
  score: PreferenceRating;
};

export type GithubContribution = {
  id: string;
  repo: string;
  contributions: number;
};

export type QiitaArticles = {
  id: string;
  qiitaID: string;
  userName: string;
  title: string;
  likedCount: number;
  publishedAt: DateLike;
};

export type ZennArticles = {
  id: string;
  zennID: string;
  userName: string;
  title: string;
  likedCount: number;
  publishedAt: DateLike;
};

export type TalentProfileMasterEngineerPosition = MasterData & {
  experienceYear?: number | null;
};

export type TalentProfileMasterEngineerSkill = MasterEngineerSkill & {
  experienceYear?: number | null;
};

export type TalentProfileWorkHistory = {
  id?: string;
  companyName: string;
  department?: string | null;
  workType?: string | null;
  projectName: string;
  jobDescription: string;
  periodFrom: DateLike;
  periodTo?: DateLike | null;
  jobPhases?: string[] | null;
  jobRole?: string | null;
  memberCount?: number | null;
  talentProfileWorkHistoryMasterEngineerPositions: MasterData[];
  talentProfileWorkHistoryMasterEngineerSkills: MasterEngineerSkill[];
};

export type TalentProfileEducationHistory = {
  id: string;
  schoolName: string;
  department?: string | null;
  graduationDate: DateLike;
};

export type TalentProfile = {
  id: string;
  age?: number | null;
  github?: string | null;
  twitter?: string | null;
  qiita?: string | null;
  zenn?: string | null;
  weeklyWorkingDayMin?: number | null;
  weeklyWorkingDayMax?: number | null;
  workingHoursMin?: number | null;
  workingHoursMax?: number | null;
  priceHourlyMax?: number | null;
  priceHourlyMin?: number | null;
  selfPromotion?: string | null;
  engagementIntent?: EngagementIntent | null;
  wantToDo?: string | null;
  workstyle?: string | null;
  availabilityStatus?: string | null;
  imageSrc?: string | null;
  introduction?: string | null;
  talentProfileMasterEngineerPositions: TalentProfileMasterEngineerPosition[];
  talentProfileMasterEngineerSkills: TalentProfileMasterEngineerSkill[];
  talentProfileWorkHistories: TalentProfileWorkHistory[];
  talentProfileEducationHistories: TalentProfileEducationHistory[];
  talentProfilePreferences: Preference[];
  githubContributions: GithubContribution[];
  qiitaArticles: QiitaArticles[];
  zennArticles: ZennArticles[];
};

export type Agreement = {
  id: string;
  createdAt?: DateLike;
  updatedAt?: DateLike;
  scoutId?: string;
  priceHourly?: number | null;
  priceMonthly?: number | null;
  workingHourPerMonth?: number | null;
  startDate?: DateLike;
  initContractEndDate?: DateLike;
  scoutMessage?: { id: string } | null;
};

export type ScoutAgent = {
  id: string;
  createdAt: DateLike;
  updatedAt: DateLike;
  project: Pick<Project, 'id' | 'createdAt' | 'updatedAt' | 'name'>;
};

export type Talent = {
  id: string;
  createdAt: DateLike;
  updatedAt: DateLike;
  name: string;
  nameKana?: string | null;
  memo?: string | null;
  hasPool: boolean;
  talentProfile: TalentProfile | null;
  scouts: Array<{
    id: string;
    createdAt: DateLike;
    updatedAt: DateLike;
    project: Pick<Project, 'id' | 'createdAt' | 'updatedAt' | 'name'>;
  }>;
  scoutAgents: ScoutAgent[];
  agreements: Agreement[];
};

export type TalentConnection = {
  nodes: Talent[];
  pageInfo: PageInfo;
};

export type TalentFilter = {
  ageMax?: InputMaybe<number>;
  ageMin?: InputMaybe<number>;
  agreementOnly?: InputMaybe<boolean>;
  consideringOnly?: InputMaybe<boolean>;
  engagementIntent?: InputMaybe<Array<InputMaybe<EngagementIntent>>>;
  engineerPosition?: InputMaybe<EngineerFilter>;
  engineerSkillFramework?: InputMaybe<EngineerFilter>;
  engineerSkillLanguage?: InputMaybe<EngineerFilter>;
  excludeScoutAgentRequested?: InputMaybe<boolean>;
  excludeScouted?: InputMaybe<boolean>;
  priceHourlyMax?: InputMaybe<number>;
  priceHourlyMin?: InputMaybe<number>;
  sort?: InputMaybe<TalentSort>;
  text?: InputMaybe<string>;
  textMode?: InputMaybe<SearchType>;
  weeklyWorkingDayMax?: InputMaybe<number>;
  weeklyWorkingDayMin?: InputMaybe<number>;
};

export type TalentListQuery = {
  talents: TalentConnection;
};

export type TalentDetailQuery = {
  talent?: Talent | null;
};

export type ScoutMessage = {
  id: string;
  createdAt: DateLike;
  updatedAt?: DateLike;
  message: string;
  messageType: MessageTypeEnum;
  organizationUser?: Pick<OrganizationUser, 'id' | 'name'> | null;
  talent?: Pick<Talent, 'id' | 'name'> | null;
  agreement?: Agreement | null;
  scout: {
    id: string;
    project: Pick<Project, 'id' | 'name'>;
    talent: Pick<Talent, 'id' | 'name'> & {
      talentProfile: Pick<
        TalentProfile,
        'id' | 'weeklyWorkingDayMin' | 'weeklyWorkingDayMax' | 'priceHourlyMax' | 'priceHourlyMin' | 'selfPromotion' | 'engagementIntent'
      > | null;
    };
  };
};

export type Scout = {
  id: string;
  createdAt: DateLike;
  updatedAt: DateLike;
  unreadOrganizationCount: number;
  latestMessage?: Pick<ScoutMessage, 'id' | 'createdAt' | 'message' | 'messageType'> | null;
  messages: ScoutMessage[];
  organization: Pick<Organization, 'id' | 'name'>;
  project: Pick<
    Project,
    | 'id'
    | 'createdAt'
    | 'updatedAt'
    | 'name'
    | 'status'
    | 'details'
    | 'requiredSkill'
    | 'welcomeSkill'
    | 'teamComposition'
    | 'targetAgeMax'
    | 'targetAgeMin'
    | 'startDate'
    | 'expectedDuration'
    | 'weeklyWorkingDayMin'
    | 'weeklyWorkingDayMax'
    | 'interviewCount'
    | 'priceTaxType'
    | 'priceHourlyMax'
    | 'priceHourlyMin'
    | 'priceMonthlyMax'
    | 'priceMonthlyMin'
    | 'priceSettlementHourFrom'
    | 'priceSettlementHourTo'
    | 'paymentDate'
  >;
  talent: Pick<Talent, 'id' | 'name'> & { talentProfile?: TalentProfile | null };
};

export type ScoutConnection = {
  nodes: Scout[];
  pageInfo: PageInfo;
};

export type ScoutsFilter = {
  message?: InputMaybe<string>;
  projectId?: InputMaybe<string>;
  sort?: InputMaybe<ScoutSortEnum>;
  unReadOnly?: InputMaybe<boolean>;
  unReplyOnly?: InputMaybe<boolean>;
};

export type MessageListQuery = {
  scouts: ScoutConnection;
};

export type ScoutMessageQuery = {
  me: Pick<OrganizationUser, 'id' | 'name'>;
  scout: Scout;
};

export type OrganizationUserSelectQuery = {
  organizationUsers: {
    nodes: Array<Pick<OrganizationUser, 'id' | 'createdAt' | 'updatedAt' | 'name'>>;
    pageInfo: PageInfo;
  };
};

export type SendScoutMessageInput = {
  scoutId: string;
  organizationUserId: string;
  message: string;
};

export type CreateAgreementInput = {
  projectId: string;
  talentId: string;
  scoutId: string;
  priceHourly?: InputMaybe<number>;
  priceMonthly?: InputMaybe<number>;
  workingHourPerMonth: number;
  startDate: DateLike;
  initContractEndDate: DateLike;
};

export type UpdateOrganizationInput = {
  address?: InputMaybe<string>;
  averageAge?: InputMaybe<number>;
  businessDetails?: InputMaybe<string>;
  capital?: InputMaybe<number>;
  ceoName?: InputMaybe<string>;
  employeeCount?: InputMaybe<number>;
  establishmentMonth?: InputMaybe<number>;
  establishmentYear?: InputMaybe<number>;
  file?: InputMaybe<File>;
  github?: InputMaybe<string>;
  logoImage?: InputMaybe<File>;
  missionVision?: InputMaybe<string>;
  name?: InputMaybe<string>;
  numberOfEmployees?: InputMaybe<number>;
  ourTeam?: InputMaybe<string>;
  postalCode?: InputMaybe<string>;
  revenue?: InputMaybe<number>;
  serviceProduct?: InputMaybe<string>;
  siteUrl?: InputMaybe<string>;
  techblogUrl?: InputMaybe<string>;
  whatWeDo?: InputMaybe<string>;
};

export type UpdateOrganizationUserInput = {
  id: string;
  email: string;
  name: string;
};

export type PasswordResetOrganizationUserInput = {
  email: string;
};

export type ScoutTemplateFilter = {
  isPrivate?: InputMaybe<boolean>;
  keyword?: InputMaybe<string>;
  onlyMine?: InputMaybe<boolean>;
  sort?: InputMaybe<TemplateSortEnum>;
};
